import { useNavigate } from 'react-router-dom';

import { Avatar, Box, Card, CardContent, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import { useMenuSelection } from 'global/MenuSelectionContext';
import { useUser } from 'global/UserContext';
import { useWebSocket } from 'global/WebSocketContext';
import { useMenuRoutes } from 'hooks/useMenuRoutes';
import { useUserProfileImage } from 'pages/IndividualBio/hooks/useUserProfileImage';
import { Notification } from 'types/notificationsTypes';

interface NotificationCardProps {
  notification: Notification;
  isDrawer: boolean;
}

const NotificationCard = ({ notification, isDrawer = false }: NotificationCardProps) => {
  const theme = useTheme();
  const { user } = useUser();
  const { imageUrl, loading: loadingImage } = useUserProfileImage(
    notification.data.requester_id || notification.data.avatar_user_id
  );

  const { sendMessage } = useWebSocket();

  const menuRoutes = useMenuRoutes();
  const { selectMenuByDocumentId } = useMenuSelection();
  const navigate = useNavigate();

  const timestampSinceSent = new Date(notification.sent_at).getTime();
  const now = new Date().getTime();
  const diffInSeconds = Math.floor((now - timestampSinceSent) / 1000) + 3600 * 3;
  const diffInMinutes = Math.floor(diffInSeconds / 60);
  const diffInHours = Math.floor(diffInMinutes / 60);
  const diffInDays = Math.floor(diffInHours / 24);

  const getTimeSinceSent = () => {
    if (diffInDays > 0) {
      return `Há ${diffInDays} dia${diffInDays > 1 ? 's' : ''}`;
    }
    if (diffInHours > 0) {
      return `Há ${diffInHours} hora${diffInHours > 1 ? 's' : ''}`;
    }
    if (diffInMinutes > 0) {
      return `Há ${diffInMinutes} minuto${diffInMinutes > 1 ? 's' : ''}`;
    }
    return 'Agora';
  };

  const timeSinceSent = getTimeSinceSent();

  const handleClick = () => {
    sendMessage({
      action: 'MARK_AS_READ',
      data: { notification_ids: [notification.id] },
    });

    if (notification.origin.includes('meeting_request')) {
      const route = menuRoutes.find(r => r.documentId === 'vpquicugxm4w5svpjji0b3ox');
      if (route) {
        const menuContext = {
          documentId: route.documentId,
          path: route.path,
          preserveLevel: true,
        };

        selectMenuByDocumentId(route.documentId, true, undefined, route.path, true);
        navigate(route.path, {
          state: {
            menuContext,
          },
        });
      }
    } else if (notification.origin.includes('edge.created')) {
      const route = menuRoutes.find(r => r.documentId === 'c7jp5768k53sra3u6bv5ynxg');
      if (route) {
        const menuContext = {
          documentId: route.documentId,
          path: route.path,
          preserveLevel: true,
        };

        selectMenuByDocumentId(route.documentId, true, undefined, route.path, true);
        navigate(route.path, {
          state: {
            selectedUser: notification.data.from_user_uuid,
            menuContext,
          },
        });
      }
    }
  };

  const renderImage = () => {
    if (notification.origin.includes('meeting_request')) {
      const userAvatar =
        notification.data.requester_id === user?.id
          ? notification.data.host_id
          : notification.data.requester_id;
      const newImageUrl = imageUrl?.replace(user?.id || '', userAvatar);
      return (
        <Avatar
          src={newImageUrl ?? undefined}
          alt={userAvatar}
          sx={{ width: 28, height: 28, justifySelf: 'flex-end' }}
        />
      );
    }
    return (
      <Avatar
        src={imageUrl ?? undefined}
        alt={notification.data.avatar_user_id}
        sx={{ width: 28, height: 28, justifySelf: 'flex-end' }}
      />
    );
  };

  return (
    <Card
      sx={{
        backgroundColor: notification.is_read
          ? theme.palette.background.paper
          : `${theme.palette.background.default}`,
        width: '100%',
        marginBottom: '0.5rem',
        cursor: 'pointer',
        paddingY: 1,
        paddingX: 0,
        borderRadius: 0,
        margin: 0,
      }}
      onClick={handleClick}
    >
      <CardContent
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'flex-start',
          justifyContent: 'flex-start',
          padding: '0',
          paddingBottom: '0',
          '&:last-child': {
            paddingBottom: '0',
          },
          '&:first-child': {
            paddingTop: '0',
          },
        }}
      >
        <Box
          sx={{
            width: '4rem',
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            alignSelf: 'flex-start',
            justifyContent: 'space-between',
            paddingX: 1,
          }}
        >
          {notification.is_read ? null : (
            <Typography variant="body1" color="primary" sx={{ fontSize: '2rem' }}>
              •
            </Typography>
          )}
          <Box sx={{ flex: 1 }} />
          {renderImage()}
        </Box>
        <Box
          sx={{
            marginLeft: 1,
          }}
        >
          <Typography variant="body1" fontWeight="bold">
            {notification.title}
          </Typography>
          <Typography variant="body2" sx={{ paddingRight: '5%' }}>
            {notification.message}
          </Typography>
          <Typography variant="caption" color="primary.main">
            {timeSinceSent}
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
};

export default NotificationCard;
